import React, { useState, useEffect } from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
const { FiSliders, FiShield, FiGlobe, FiClock, FiActivity, FiAlertCircle, FiSave, FiLock, FiEye, FiLoader, FiRefreshCw, FiToggleRight, FiServer, FiDatabase, FiLayers } = FiIcons;

const showToast = (message, type = 'success') => {
  window.dispatchEvent(new CustomEvent('show-toast', { detail: { message, type } }));
};

const SettingsSection = ({ title, icon, children }) => (
  <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden mb-6">
    <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-2 bg-slate-50/50">
      <SafeIcon icon={icon} className="text-slate-400" />
      <h3 className="font-bold text-slate-800 text-sm">{title}</h3>
    </div>
    <div className="p-6 space-y-5">
      {children}
    </div>
  </div>
);

const ToggleField = ({ label, description, enabled, onToggle }) => (
  <div className="flex items-center justify-between py-2">
    <div className="space-y-0.5">
      <p className="text-xs font-bold text-slate-700">{label}</p>
      <p className="text-[10px] text-slate-500">{description}</p>
    </div>
    <button onClick={onToggle} className={`w-10 h-5 rounded-full p-1 transition-all ${enabled ? 'bg-blue-600' : 'bg-slate-300'}`} >
      <div className={`w-3 h-3 bg-white rounded-full transition-all transform ${enabled ? 'translate-x-5' : 'translate-x-0'}`}></div>
    </button>
  </div>
);

const Settings = () => {
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isLive, setIsLive] = useState(false);
  const [isUsingDefaults, setIsUsingDefaults] = useState(false);
  const [error, setError] = useState(null);
  
  const [settings, setSettings] = useState({
    fallback_mode: 'automatic',
    maintenance_mode: false,
    daily_cap: 10000,
    monthly_cap: 250000,
    notes: ''
  });

  const callGateway = async (action, payload = {}) => {
    try {
      const response = await fetch('/.netlify/functions/gateway', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action, payload })
      });
      
      const text = await response.text();
      let result;
      try {
        result = JSON.parse(text);
      } catch (e) {
        console.error(`Gateway Debug [${action}]:`, { status: response.status, origin: window.location.origin });
        throw new Error(`Invalid JSON: ${response.status}`);
      }

      if (!result.success) {
        // Log diagnostic data if available
        if (result.errorCode === 'POCKETBASE_REQUEST_FAILED') {
          console.group(`PocketBase Schema Error: ${action}`);
          console.error('Status:', result.data.status);
          console.error('Details:', result.data.details);
          console.error('Fields Attempted:', result.data.sentFields);
          console.groupEnd();
        }
        throw new Error(result.message || 'Gateway Error');
      }
      return result.data;
    } catch (err) {
      console.error(`Gateway Action [${action}] failed:`, err.message);
      throw err;
    }
  };

  const fetchSettings = async () => {
    setLoading(true);
    setError(null);
    setIsUsingDefaults(false);
    try {
      const pbData = await callGateway('list_settings');
      if (pbData && pbData.length > 0) {
        const item = pbData[0];
        setSettings({
          fallback_mode: item.fallback_mode || 'automatic',
          maintenance_mode: !!item.maintenance_mode,
          daily_cap: Number(item.daily_cap) || 10000,
          monthly_cap: Number(item.monthly_cap) || 250000,
          notes: item.notes || ''
        });
        setIsLive(true);
      } else {
        setIsLive(true);
        setIsUsingDefaults(true);
      }
    } catch (err) {
      setError(`Backend unavailable: ${err.message}`);
      setIsLive(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const update = (key, val) => setSettings(prev => ({ ...prev, [key]: val }));

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await callGateway('save_settings', settings);
      showToast('Global settings saved to PocketBase');
      fetchSettings();
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6 animate-in slide-in-from-bottom-4 duration-500 pb-20 relative">
      {loading && (
        <div className="absolute inset-0 bg-slate-50/60 backdrop-blur-[1px] z-10 flex items-center justify-center min-h-[400px]">
           <SafeIcon icon={FiLoader} className="text-3xl text-blue-600 animate-spin" />
        </div>
      )}

      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-800 flex items-center gap-3">
            <SafeIcon icon={FiSliders} className="text-blue-600" /> Global Gateway Settings
          </h2>
          <div className="flex items-center gap-2 mt-1">
            {isLive ? (
              <span className="text-[10px] bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 rounded-full font-bold flex items-center gap-1">
                <SafeIcon icon={FiDatabase} /> LIVE VIA POCKETBASE
              </span>
            ) : (
              <span className="text-[10px] bg-amber-50 text-amber-700 border border-amber-200 px-2 py-0.5 rounded-full font-bold flex items-center gap-1">
                <SafeIcon icon={FiLayers} /> USING LOCAL DEFAULTS
              </span>
            )}
            {isUsingDefaults && isLive && (
              <span className="text-[10px] text-blue-600 font-bold italic">Using local defaults until settings are saved.</span>
            )}
            {error && <span className="text-[10px] text-red-500 font-medium italic">({error})</span>}
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={fetchSettings} className="p-2.5 bg-white border border-slate-200 text-slate-500 rounded-xl hover:bg-slate-50 transition-colors shadow-sm">
            <SafeIcon icon={FiRefreshCw} className={loading ? 'animate-spin' : ''} />
          </button>
          <button onClick={handleSave} disabled={isSaving || loading} className="px-6 py-2.5 bg-blue-600 text-white text-sm font-bold rounded-xl hover:bg-blue-700 transition-all shadow-md flex items-center gap-2 disabled:opacity-50">
            <SafeIcon icon={isSaving ? FiLoader : FiSave} className={isSaving ? 'animate-spin' : ''} />
            {isSaving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <SettingsSection title="Routing & Providers" icon={FiServer}>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Fallback Mode</label>
              <div className="grid grid-cols-2 gap-2">
                {['automatic', 'manual'].map(mode => (
                  <button key={mode} onClick={() => update('fallback_mode', mode)} className={`py-2 text-xs font-bold rounded-lg border transition-all capitalize ${settings.fallback_mode === mode ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-500 border-slate-200'}`} >{mode}</button>
                ))}
              </div>
            </div>
            <ToggleField label="Maintenance Mode" description="Reject all non-admin requests" enabled={settings.maintenance_mode} onToggle={() => update('maintenance_mode', !settings.maintenance_mode)} />
          </div>
        </SettingsSection>

        <SettingsSection title="Global Quotas" icon={FiActivity}>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Daily Cap</label>
                <input type="number" className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm" value={settings.daily_cap} onChange={(e) => update('daily_cap', e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Monthly Cap</label>
                <input type="number" className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm" value={settings.monthly_cap} onChange={(e) => update('monthly_cap', e.target.value)} />
              </div>
            </div>
          </div>
        </SettingsSection>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl p-6">
        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2">Internal Admin Notes</label>
        <textarea 
          className="w-full h-32 px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:bg-white transition-all resize-none" 
          placeholder="Notes about global gateway configuration..."
          value={settings.notes}
          onChange={(e) => update('notes', e.target.value)}
        />
      </div>
    </div>
  );
};

export default Settings;